import { history } from 'helpers/History';
import { stringSort } from 'helpers/sorters';
import { StoreModel } from 'models/Index';
import { observable, action, computed, makeObservable } from 'mobx';
import { Brand } from '../../helpers/constants/Brands';
import { RegisterModel } from '../shared/RegisterModel';
import { http } from '../../helpers/HttpApi';
import {
    LocationModel,
    LocationClass,
    LocationTypeId,
} from '../shared/LocationModel';
import { AdminModel } from '.';

export default class RegistrationModel {
    @observable
    public locations?: Array<LocationModel> = [];
    @observable
    public registers: Array<RegisterModel> = [];

    @observable
    public selectedRegister: RegisterModel = new RegisterModel();
    @observable
    public selectedBrand: Brand = Brand.OPTIMUM;
    public selectedLocation?: LocationModel;

    @observable
    public currentRegister?: RegisterModel;

    @observable
    public registersLoaded: boolean = false;

    public brands: Array<string> = Object.values(Brand);

    root: StoreModel;
    parent: AdminModel;

    constructor(root: StoreModel, parent: AdminModel) {
        this.root = root;
        this.parent = parent;

        makeObservable(this);
    }

    @action getLocations = async () => {
        const locationsResponse = await http.get<Array<LocationModel>>(
            '/api/locations'
        );
        const locations = locationsResponse.data;

        locations?.forEach(
            (l, _, locations) =>
                (l.parentName =
                    locations.find((p) => p.locationId === l.parentId)
                        ?.displayName || '')
        );

        this.locations = locations
            ?.filter(
                (l) =>
                    l.isActive &&
                    l.locationClass === LocationClass[LocationClass.Store] &&
                    l.locationTypeId === LocationTypeId[LocationTypeId.Retail]
            )
            .sort((a, b) => {
                /**
                 * We should sort locations by parentName because we are doing `groupBy` in material Autocomplete.
                 * Sorting by `groupBy` property is required for keeping valid indexes in Autocomplete options...
                 * See warning in Autocomplete https://github.com/mui-org/material-ui/blob/v4.10.2/packages/material-ui-lab/src/useAutocomplete/useAutocomplete.js#L912
                 */
                if (a.parentName !== b.parentName) {
                    return a.parentName < b.parentName ? -1 : 1;
                }

                return a.displayName > b.displayName
                    ? 1
                    : a.displayName < b.displayName
                    ? -1
                    : 0;
            });
    };

    @action getRegisters = async (locationId: number) => {
        const registersResponse = await http.get<Array<RegisterModel>>(
            `/api/locations/${locationId}/cash-registers`
        );
        let registers = registersResponse.data;

        if (this.currentRegister && this.root.locationId === locationId) {
            registers = [this.currentRegister, ...(registers || [])];
        }

        this.registers = registers
            ? registers.sort((current, next) =>
                  stringSort(current.registerName, next.registerName)
              )
            : [];
        this.registersLoaded = true;
    };

    @action getRegisterByName = (locationId: number, registerName: string) => {
        return http
            .get<RegisterModel>(
                `/api/locations/${locationId}/cash-registers/${registerName}`
            )
            .then((response) => response.data);
    };

    @action processAlticeKioskRegister = async () => {
        this.selectedRegister.brand = this.selectedBrand;

        await this.selectedRegister.createRegister();
        await this.root.collection.openRegister(
            this.selectedRegister.registerName,
            this.selectedRegister.locationId
        );

        await this.root.initShellValues({
            locationId: this.selectedRegister.locationId,
            locationName: this.selectedLocation?.displayName,
            registerName: this.selectedRegister.registerName,
            isRegisterEnabled: this.selectedRegister.isActive,
            brand: this.selectedBrand as string,
            kioskName: this.selectedRegister.kioskName,
        });

        // Here we try to prevent double initialization of message bus
        // TODO: @b2bsoft/kiosk-shell-sdk should handle this...
        if (!this.isKioskInitiallyRegistered) {
            await this.root.initializeMessageBus();
        }

        history.push(`/${window.location.search}`);
    };

    @action getKioskDisplayName = (
        locationId: number,
        registerName: string,
        registerId: number | null
    ) => {
        return `${registerName}K${registerId}S${locationId}`;
    };

    @action getExistingRegister = async () => {
        if (!this.root.locationId || !this.root.registerName) {
            return;
        }

        const locationId = Number(this.root.locationId);

        //get existing register by standalone call
        const existingRegister = await this.getRegisterByName(
            locationId,
            this.root.registerName
        );

        if (existingRegister) {
            this.currentRegister = existingRegister;

            this.selectedBrand = existingRegister.brand || this.root.brand;
            this.selectedLocation = this.locations?.find(
                (l) => l.locationId === locationId
            );
            this.setSelectedRegister(existingRegister, true);
        }

        if (this.selectedLocation) {
            this.getRegisters(this.selectedLocation.locationId);
        }
    };

    @action reset = () => {
        this.locations = [];
        this.registers = [];
        this.registersLoaded = false;
        this.selectedLocation = undefined;
    };

    @action
    setValue(value: any) {
        Object.assign(this, value);
    }

    @action handleLocationChange = async (location: LocationModel | null) => {
        if (!location) {
            return;
        }

        this.selectedLocation = location;
        this.selectedRegister.registerId = null;

        await this.getRegisters(location.locationId);

        this.isSingleRegister && this.setSelectedRegister(this.registers![0]);
    };

    @action handleRegisterChange = (registerId: number) => {
        const selectedRegister = this.registers.find(
            (r) => r.registerId === registerId
        );

        if (selectedRegister) {
            this.setSelectedRegister(selectedRegister!);
        }
    };
    @action handleBrandChange = (brand: string) => {
        this.selectedBrand = brand;
    };

    @action setSelectedRegister = (
        register: RegisterModel,
        setIsActive: boolean | null = null
    ) => {
        if (setIsActive) this.selectedRegister.isActive = register.isActive;

        this.selectedRegister.locationId = this.selectedLocation?.locationId!;
        this.selectedRegister.registerName = register?.registerName!;
        this.selectedRegister.registerId = register?.registerId!;
        this.selectedRegister.kioskId = this.root.kioskId!;
        this.selectedRegister.kioskName = this.getKioskDisplayName(
            this.selectedRegister.locationId,
            this.selectedRegister.registerName,
            this.selectedRegister.registerId
        );
        this.selectedRegister.brand = this.selectedBrand;
    };

    @action handleSwitchChange = (checked: boolean) =>
        this.selectedRegister?.setValue({ isActive: checked });

    @computed get selectedRegisterLocationName() {
        return this.selectedRegister.locationId
            ? this.locations?.find(
                  (l) => l.locationId === this.selectedRegister?.locationId
              )?.displayName
            : '';
    }

    get isSingleRegister(): Boolean {
        return this.registers.length === 1;
    }

    /**
     * Flag which indicates if kiosk was registered before.
     * So if it's true, means that kiosk is currently registered, and it was registered before making another register operation.
     * @readonly
     * @memberof RegistrationModel
     */
    @computed
    get isKioskInitiallyRegistered() {
        return !!this.currentRegister;
    }
}
